/**
 * ONE ORGANIZED LOADING STATE, OR NONE AT ALL.
 *
 * A Collection tab whose finished content is not ready on the frame the
 * player picked it holds its body instead of painting it piece by piece. The
 * body stays laid out (so every measurement the build takes — page sizes, card
 * fits — is the real one) but unpainted, and it is revealed in ONE frame when
 * the owner calls `release()`. The Decks tab used to show an empty chooser,
 * then four summoner cards 800 ms later that pushed the pager and saved decks
 * down 248 px, then the saved decks behind skeletons; the Moon Exchange showed
 * skeleton rows whose shimmer itself shifted the layout. The owner (24 Sep
 * 2026): "one organized loading state is the most a screen may show, and
 * avoiding it is better still".
 *
 * So: a warm tab is revealed within a frame or two and never shows a loading
 * state. Only a wait that outlasts `SURFACE_LOADING_STATE_DELAY_MS` (for a warm
 * entry, the shared grace) puts up the ONE loading state — a spinner and a
 * translated line, announced politely and marked `data-loading` so the
 * snappiness meter counts it for what it is.
 *
 * A hold is a readiness gate, so it is capped (docs/asset-loading.md law 2).
 * The cap is armed by the owner once there is content to show (`armCap`): a
 * surface whose data has not answered has nothing to reveal, and taking the
 * hold away early would only paint an empty frame; a failed read releases the
 * hold through the owner's error path instead.
 */

/* Long enough that a prepared tab — a few frames to commit — never flashes
   the loading state; short enough that a real wait is answered inside the
   100 ms a press gets before it must be acknowledged. */
const SURFACE_LOADING_STATE_DELAY_MS = 80;

/* The shared `--card-art-grace` (docs/asset-loading.md, the grace rule): a
   loading treatment stays unseen this long, so a body that commits inside it
   appears whole with no loading state at all. For a WARM entry only — one whose
   reads have already answered — where the few-frames state would just flash
   in front of the body (a placeholder flashing on a warm load): the Moon
   Exchange's warm entry commits after a few hundred milliseconds of raster
   work, and the 80 ms state flashed on every warm visit (round 13, 25 September
   2026). A cold entry keeps the few-frames delay: its wait is real, and where
   the pressed control's own look is not seen (Firefox read no pressed look on
   about half the Collection's tab presses), the state's first frame is the
   only visible answer the press gets. */
const SURFACE_LOADING_GRACE_MS = 450;

/* A release for these reasons reveals nothing: the body is handed straight to
   a new hold in the same task ('superseded'), or it is leaving ('close'). */
const RELEASES_WITHOUT_REVEAL = new Set(['superseded', 'close']);

/**
 * The hold hides the body with `visibility: hidden`, which every descendant
 * inherits, and main.css transitions `all` on every button: the release's own
 * class change starts a visibility transition on each of them, and its first
 * frame is its start value, hidden — so the controls arrived one frame after
 * the rest of the body in Chrome, Firefox and WebKit. Finish exactly those
 * transitions — visibility, ending visible (reading the animations computes
 * the release's style change) — and leave every other transition running: an
 * entrance, a colour, or a designed hide delay (visible -> hidden) started in
 * the same task.
 *
 * Only a release that reveals the body does this. Reading the animations
 * flushes style: a release handed straight to a new hold ('superseded') then
 * committed the visible look first, and the re-hold started a visible -> hidden
 * transition on every button, which kept the controls painted over the held
 * body for the whole 0.2-0.3 s in Chrome, Firefox and WebKit. Without the
 * flush the two class changes in one task cancel out.
 */
function revealsVisibility(animation) {
    if (animation?.transitionProperty !== 'visibility') return false;
    try {
        const frames = animation.effect?.getKeyframes?.();
        const last = Array.isArray(frames) && frames.length > 0 ? frames[frames.length - 1] : null;
        return !last?.visibility || last.visibility === 'visible';
    } catch (_) {
        return true;
    }
}

/* Finishing a parent's reveal can start a nested one (a `transition: all`
   child inherits hidden -> visible only once its parent is visible), so the
   passes repeat until none is left, bounded like the Collection's own. */
const REVEAL_TRANSITION_PASSES = 4;

function finishRevealTransitions(host) {
    if (!host?.isConnected || typeof host.getAnimations !== 'function') return;
    const finished = new Set();
    for (let pass = 0; pass < REVEAL_TRANSITION_PASSES; pass += 1) {
        let pending = [];
        try {
            pending = host.getAnimations({ subtree: true })
                .filter((animation) => !finished.has(animation) && revealsVisibility(animation));
        } catch (_) {
            return;
        }
        if (pending.length === 0) return;
        pending.forEach((animation) => {
            finished.add(animation);
            try {
                animation.finish();
            } catch (_) {
                // An engine may refuse to finish one; the next frame still shows it.
            }
        });
    }
}

/**
 * @param {HTMLElement|null} host the surface body to hold
 * @param {object} [options]
 * @param {string|(() => string)} [options.label] the loading line, resolved
 *   when it is shown so it is in the player's current language
 * @param {((reason: string) => void)|null} [options.onRelease]
 * @param {number} [options.loadingDelayMs] how long the body is held before
 *   the loading state appears (`SURFACE_LOADING_GRACE_MS`, the shared grace,
 *   for a warm entry only)
 */
function holdSurfaceEntry(host, {
    label = '',
    onRelease = null,
    loadingDelayMs = SURFACE_LOADING_STATE_DELAY_MS,
} = {}) {
    let active = Boolean(host);
    let status = null;
    let delayTimer = null;
    let capTimer = null;

    const showStatus = () => {
        delayTimer = null;
        if (!active || status) return;
        status = document.createElement('div');
        status.className = 'surface-entry-loading';
        status.dataset.loading = 'true';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        const spinner = document.createElement('span');
        spinner.className = 'surface-entry-loading-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        const text = document.createElement('span');
        text.className = 'surface-entry-loading-label';
        text.textContent = typeof label === 'function' ? label() : String(label || '');
        status.append(spinner, text);
        host.appendChild(status);
    };

    const hold = {
        get active() {
            return active;
        },
        /** Start (or restart) the bounded wait for the content's media. */
        armCap(capMs) {
            if (!active) return;
            if (capTimer !== null) clearTimeout(capTimer);
            capTimer = setTimeout(() => {
                capTimer = null;
                hold.release('cap');
            }, Math.max(0, Number(capMs) || 0));
        },
        /** Reveal the body in this task. Idempotent; true when it was held. */
        release(reason = 'ready') {
            if (!active) return false;
            active = false;
            if (delayTimer !== null) clearTimeout(delayTimer);
            if (capTimer !== null) clearTimeout(capTimer);
            delayTimer = null;
            capTimer = null;
            host.classList.remove('surface-entry-hold');
            host.removeAttribute('aria-busy');
            status?.remove();
            status = null;
            if (!RELEASES_WITHOUT_REVEAL.has(reason)) finishRevealTransitions(host);
            if (typeof onRelease === 'function') onRelease(reason);
            return true;
        },
    };

    if (!active) return hold;
    host.classList.add('surface-entry-hold');
    host.setAttribute('aria-busy', 'true');
    const delay = Number(loadingDelayMs);
    delayTimer = setTimeout(showStatus, Number.isFinite(delay) && delay >= 0 ? delay : SURFACE_LOADING_STATE_DELAY_MS);
    return hold;
}

const SurfaceEntryHold = {
    hold: holdSurfaceEntry,
    LOADING_STATE_DELAY_MS: SURFACE_LOADING_STATE_DELAY_MS,
    LOADING_GRACE_MS: SURFACE_LOADING_GRACE_MS,
};

export { SurfaceEntryHold };
